// WP-0.7 (§18.1 layout frame): top bar 56px — sidebar toggle, global search (`/` focuses),
// ＋ Quick Add (`N`), privacy toggle (Ctrl+.), status dot cluster (sync · bridge · backup),
// theme + avatar menu. Wiring for search/quick-add lands with their features (WP-0.7 stubs
// the affordances so the frame is complete).
import { Eye, EyeOff, Lock, Menu, Moon, Plus, Search, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppLock } from '../lib/appLock';
import { usePrivacy } from '../lib/privacy';
import { useTheme } from '../lib/theme';
import { TransactionForm } from './TransactionForm';

const iconButtonClass =
  'rounded-[var(--radius-control)] p-1.5 text-muted hover:bg-bg hover:text-ink active:bg-border/40';

function StatusDot({ label, color }: { label: string; color: string }): React.JSX.Element {
  return (
    <span
      title={label}
      aria-label={label}
      className="inline-block h-2.5 w-2.5 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

export function TopBar({ onToggleSidebar }: { onToggleSidebar: () => void }): React.JSX.Element {
  const { blurred, toggle } = usePrivacy();
  const { enabled: appLockEnabled, lockNow } = useAppLock();
  const { resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');

  // §18.1 keyboard: `/` focuses global search, `N` opens Quick Add.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
      if (typing) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setQuickAddOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4">
      <button aria-label="Toggle sidebar" className={iconButtonClass} onClick={onToggleSidebar}>
        <Menu size={20} />
      </button>

      <div className="relative flex-1 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input
          ref={searchRef}
          type="search"
          placeholder="Search…  (press /)"
          aria-label="Global search"
          value={searchDraft}
          onChange={(e) => {
            setSearchDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            const q = searchDraft.trim();
            if (q === '') return;
            navigate(`/search?q=${encodeURIComponent(q)}`);
            setSearchDraft(''); // the Search page now owns the query; don't leave a stale echo here
          }}
          className="w-full rounded-[var(--radius-control)] border border-border bg-bg py-1.5 pl-9 pr-3 text-sm text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--primary)_18%,transparent)]"
        />
      </div>

      <button
        className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
        title="Quick add (N)"
        onClick={() => {
          setQuickAddOpen(true);
        }}
      >
        <Plus size={16} /> Add
      </button>

      <button
        aria-label={blurred ? 'Show amounts' : 'Hide amounts'}
        title="Privacy blur (Ctrl+.)"
        className={iconButtonClass}
        onClick={toggle}
      >
        {blurred ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>

      {/* WP-C1.7 (ST-4, SE-1): only shown once a PIN is actually set — nothing to lock with
          otherwise. */}
      {appLockEnabled && (
        <button aria-label="Lock now" title="Lock now" className={iconButtonClass} onClick={lockNow}>
          <Lock size={20} />
        </button>
      )}

      <button
        aria-label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        className={iconButtonClass}
        onClick={() => {
          // Toggle off the RESOLVED appearance so the first click always changes something.
          setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
        }}
      >
        {resolvedTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      <div className="flex items-center gap-1.5" aria-label="System status">
        <StatusDot label="Sync: ok" color="var(--success)" />
        <StatusDot label="Bridge: not linked" color="var(--muted)" />
        <StatusDot label="Backup: ok" color="var(--success)" />
      </div>

      <TransactionForm open={quickAddOpen} onOpenChange={setQuickAddOpen} />
    </header>
  );
}
